/**
 * Sortable, infinitely-scrollable table listing user groups with edit functionality.
 *
 * @module components/admin/Groups
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState} from 'react';
import Paper from '@material-ui/core/Paper';
import PropTypes from 'prop-types';
import Avatar from '@material-ui/core/Avatar';
import {
    makeStyles,
} from '@material-ui/core/styles';
import InfiniteScroll from 'react-infinite-scroll-component';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import moment from 'moment';
import StyledLink from '../common/StyledLink';
import { permissionCheck } from '../../utils/permissionCheck';
import AdminsPermissions from '../../permissions/admin';
import GroupsStyle from './Groups';

/**
 * Represents a functional React component used for rendering a table of groups with sorting and infinite scrolling capabilities.
 *
 * @param {Object} props - The props object for the `Groups` component.
 * @param {Array} props.groupList - The list of groups to be displayed in the table.
 * @param {number} props.total - The total number of groups available for infinite scrolling purposes.
 * @param {Function} props.handleNext - The function to handle fetching the next chunk of groups for infinite scrolling.
 * @param {Function} props.sortProperties - The function responsible for sorting groups by the specified property.
 * @param {string} props.sortBy - The property key currently used for sorting the groups.
 * @param {string} props.order - The sorting order, either 'asc' for ascending or 'desc' for descending.
 * @returns {JSX.Element} - A React component that renders a sortable and scrollable table of groups.
 */
const Groups = ({
    groupList,
    total,
    handleNext,
    sortProperties,
    sortBy,
    order,
}) => {
    const useStyles = makeStyles((theme) => GroupsStyle(theme));
    const classes = useStyles();
    const [sort, setSort] = useState(false);

    useEffect(() => {
        if (groupList.length && sort) {
            setSort(false);
        }
    }, [groupList.length, sort]);

    /**
     * A function that handles change events by invoking the `handleNext` function.
     * This function is typically used as a callback to respond to user interactions or updates.
     */
    const handleChange = () => {
        handleNext();
    };

    //console.log('Groups = %o', groupList);

    return (
        <InfiniteScroll
            dataLength={groupList.length}
            next={handleChange}
            hasMore={groupList.length < total}
            loader={<p>Loading...</p>}
        >
            <TableContainer component={Paper}>
                <Table aria-label="Groups Table" className={classes.root}>
                    <TableHead className={classes.thead}>
                        <TableRow className={classes.tableRow}>
                            <TableCell className={classes.heading}>
                                <TableSortLabel
                                    direction={sortBy === 'name' ? order : 'asc'}
                                    active
                                    IconComponent={ArrowDropDownIcon}
                                    onClick={() => {
                                        sortProperties('name');
                                        setSort(true);
                                    }}
                                >
                                    NAME
                                </TableSortLabel>
                            </TableCell>
                            <TableCell className={classes.heading}>
                                <TableSortLabel
                                    direction={sortBy === 'description' ? order : 'asc'}
                                    active
                                    IconComponent={ArrowDropDownIcon}
                                    onClick={() => {
                                        sortProperties('description');
                                        setSort(true);
                                    }}
                                >
                                    DESCRIPTION
                                </TableSortLabel>
                            </TableCell>
                            <TableCell className={classes.heading}>
                                <TableSortLabel
                                    direction={sortBy === 'created' ? order : 'asc'}
                                    active
                                    IconComponent={ArrowDropDownIcon}
                                    onClick={() => {
                                        sortProperties('created');
                                        setSort(true);
                                    }}
                                >
                                    CREATED
                                </TableSortLabel>
                            </TableCell>
                            <TableCell className={classes.heading}>
                                <TableSortLabel
                                    direction={sortBy === 'updated' ? order : 'asc'}
                                    active
                                    IconComponent={ArrowDropDownIcon}
                                    onClick={() => {
                                        sortProperties('updated');
                                        setSort(true);
                                    }}
                                >
                                    LAST UPDATED
                                </TableSortLabel>
                            </TableCell>
                            <TableCell className={classes.heading}>
                                <TableSortLabel
                                    direction={sortBy === 'owner' ? order : 'asc'}
                                    active
                                    IconComponent={ArrowDropDownIcon}
                                    onClick={() => {
                                        sortProperties('owner');
                                        setSort(true);
                                    }}
                                >
                                    GROUP OWNER
                                </TableSortLabel>
                            </TableCell>
                            <TableCell className={classes.heading}>
                                <TableSortLabel
                                    direction={sortBy === 'members' ? order : 'asc'}
                                    active
                                    IconComponent={ArrowDropDownIcon}
                                    onClick={() => {
                                        sortProperties('members');
                                        setSort(true);
                                    }}
                                >
                                    NO. OF MEMBERS
                                </TableSortLabel>
                            </TableCell>
                            <TableCell className={classes.heading}>
                                <TableSortLabel
                                    direction={sortBy === 'creator' ? order : 'asc'}
                                    active
                                    IconComponent={ArrowDropDownIcon}
                                    onClick={() => {
                                        sortProperties('creator');
                                        setSort(true);
                                    }}
                                >
                                    CREATED BY
                                </TableSortLabel>
                            </TableCell>
                            {
                                permissionCheck(
                                    AdminsPermissions.groups.update.task,
                                    AdminsPermissions.groups.update.type,
                                )
                                    ? (
                                        <TableCell className={classes.heading}>
                                            EDIT
                                        </TableCell>
                                    )
                                    : null
                            }

                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groupList.length > 0 && groupList.map((row) => (
                            <TableRow
                                key={row._id}
                                //       onClick={() => handleRowClick(row)}
                                className={classes.tableRow}
                            >
                                <TableCell className={classes.bold}>
                                    {row.name}
                                </TableCell>
                                <TableCell className={classes.lastNameCell}>
                                    {row.description}
                                </TableCell>
                                <TableCell className={classes.lastNameCell}>
                                    {moment(row.created_at).format('MM-DD-YYYY')}
                                </TableCell>
                                <TableCell className={classes.lastNameCell}>
                                    {moment(row.updated_at).format('MM-DD-YYYY')}
                                </TableCell>
                                <TableCell>
                                    <div className={classes.built}>
                                        <Avatar
                                            src={row.owner?.profile_pic ?? ''}
                                            alt="profile"
                                            style={{ marginRight: '1em' }}
                                            className={classes.small}
                                        />
                                        <div className={classes.firstNameCell}>{row.owner.name}</div>
                                    </div>
                                </TableCell>
                                <TableCell align="center" className={classes.no_of_members}>
                                    {row.member_count}
                                </TableCell>
                                <TableCell align="left" className={classes.lastNameCell}>{row.created_by}</TableCell>
                                {
                                    permissionCheck(
                                        AdminsPermissions.groups.update.task,
                                        AdminsPermissions.groups.update.type,
                                    )
                                        ? (
                                            <TableCell>
                                                <StyledLink to={{
                                                    pathname: '/admin',
                                                    state: row,
                                                }}
                                                >
                                                    <EditOutlinedIcon className={classes.editIcon} fontSize="small" />
                                                </StyledLink>
                                            </TableCell>
                                        )
                                        : null
                                }

                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </InfiniteScroll>
    );
};

Groups.propTypes = {
    groupList: PropTypes.array,
    total: PropTypes.number,
    handleNext: PropTypes.func,
    sortProperties: PropTypes.func,
    sortBy: PropTypes.string,
    order: PropTypes.string,
};

export default Groups;
